import { useState, useEffect } from "react";
import axios from "axios";
import HouseholdCheckinPrompt from "@/components/HouseholdCheckinPrompt";
import { RolePicker, GuestForm } from "@/components/checkin/RolePicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pub = axios.create({ baseURL: API });

const box = "w-full bg-white border border-ink/20 rounded-xl px-4 py-3 min-h-[52px] text-[17px]";
const bigBtn = "w-full rounded-xl px-5 py-4 min-h-[64px] text-[18px] font-semibold flex items-center justify-center gap-2";

function Success({ name, already, onDone }) {
  return (
    <div className="text-center py-10" data-testid="checkin-success">
      <div className="w-16 h-16 rounded-full bg-olive/25 text-dark-olive flex items-center justify-center mx-auto mb-4 text-3xl">✓</div>
      <h2 className="font-zilla text-3xl font-semibold mb-1">{already ? "You're already checked in" : "You're checked in!"}</h2>
      <p className="text-ink/70 text-[17px] mb-8">{already ? `Thank you${name ? `, ${name}` : ""}.` : `Welcome${name ? `, ${name}` : ""}. Enjoy your visit.`}</p>
      <button data-testid="checkin-done" onClick={onDone} className="border border-ink/25 rounded-xl px-6 py-3 min-h-[52px] text-[16px] font-semibold">Check someone else in</button>
    </div>
  );
}

function MemberForm({ onSuccess }) {
  const [type, setType] = useState("number");
  const [id, setId] = useState("");
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const ph = { number: "RCA-0002", phone: "Phone number", email: "you@email.com" }[type];
  const submit = async () => {
    if (!id.trim() || pin.length !== 4) { setErr("Enter your details and 4-digit PIN."); return; }
    setErr(""); setBusy(true);
    try {
      const r = await pub.post("/checkin/member", { identifier_type: type, identifier: id, pin });
      onSuccess(r.data);
    } catch (e) { setErr(e.response?.data?.detail || "Could not check you in."); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="member-form">
      <div className="grid grid-cols-3 gap-2">
        {["number", "phone", "email"].map((t) => (
          <button key={t} data-testid={`id-type-${t}`} onClick={() => setType(t)} className={`rounded-lg px-2 py-2.5 min-h-[48px] text-[14px] font-semibold capitalize border ${type === t ? "bg-ink text-cream border-ink" : "border-ink/25"}`}>{t === "number" ? "Member #" : t}</button>
        ))}
      </div>
      <input data-testid="member-identifier" value={id} onChange={(e) => setId(e.target.value)} placeholder={ph} className={box} />
      <input data-testid="member-pin" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} inputMode="numeric" maxLength={4} placeholder="4-digit PIN" className={box} />
      {err && <div className="text-maroon text-[15px]" data-testid="member-error">{err}</div>}
      <button data-testid="member-submit" disabled={busy} onClick={submit} className={`${bigBtn} bg-maroon text-cream disabled:opacity-50`}>Check in</button>
    </div>
  );
}

function NewVisitorForm({ initial, onSuccess }) {
  const [f, setF] = useState({ name: "", organization: "", phone: initial?.phone || "", email: initial?.email || "" });
  const [err, setErr] = useState("");
  const submit = async () => {
    setErr("");
    try { const r = await pub.post("/checkin/visitor/new", f); onSuccess({ name: r.data.name }); }
    catch (e) { setErr(e.response?.data?.detail || "Could not check you in."); }
  };
  return (
    <div className="space-y-3" data-testid="new-visitor-form">
      <input data-testid="visitor-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name" className={box} />
      <input data-testid="visitor-org" value={f.organization} onChange={(e) => setF({ ...f, organization: e.target.value })} placeholder="Organization / business (optional)" className={box} />
      <input data-testid="visitor-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone (optional)" className={box} />
      <input data-testid="visitor-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email (optional)" className={box} />
      {err && <div className="text-maroon text-[15px]" data-testid="visitor-error">{err}</div>}
      <button data-testid="visitor-submit" onClick={submit} className={`${bigBtn} bg-dark-olive text-cream`}>Check in</button>
    </div>
  );
}

function ReturningForm({ onSuccess, onFallback }) {
  const [f, setF] = useState({ phone: "", email: "" });
  const [err, setErr] = useState("");
  const submit = async () => {
    setErr("");
    try {
      const r = await pub.post("/checkin/visitor/returning", f);
      if (r.data.matched) onSuccess(r.data);
      else onFallback(f);
    } catch (e) { setErr(e.response?.data?.detail || "Could not check you in."); }
  };
  return (
    <div className="space-y-3" data-testid="returning-form">
      <p className="text-ink/70 text-[15px]">Enter the phone or email you used before.</p>
      <input data-testid="returning-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone number" className={box} />
      <input data-testid="returning-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" className={box} />
      {err && <div className="text-maroon text-[15px]" data-testid="returning-error">{err}</div>}
      <button data-testid="returning-submit" onClick={submit} className={`${bigBtn} bg-dark-olive text-cream`}>Find me &amp; check in</button>
    </div>
  );
}

export default function PublicCheckIn() {
  const [ctx, setCtx] = useState(null);
  const [screen, setScreen] = useState("choose"); // choose | member | returning | new | household | done
  const [name, setName] = useState("");
  const [fallback, setFallback] = useState(null);
  const [household, setHousehold] = useState(null);
  const [already, setAlready] = useState(false);
  const [roles, setRoles] = useState(null);

  useEffect(() => {
    const load = () => pub.get("/checkin/station").then((r) => setCtx(r.data)).catch(() => setCtx({ active: false }));
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);
  const done = (data) => {
    const d = typeof data === "string" ? { name: data } : (data || {});
    if (d.needs_roles) { setRoles(d); setScreen("roles"); return; }
    setName(d.name || "");
    setAlready(!!d.already);
    const hh = d.household || [];
    if (!d.already && hh.length) { setHousehold({ memberId: d.member_id, list: hh }); setScreen("household"); }
    else setScreen("done");
  };
  const reset = () => { setScreen("choose"); setName(""); setFallback(null); setHousehold(null); setAlready(false); setRoles(null); };
  useEffect(() => { if (screen === "done") { const t = setTimeout(reset, 6000); return () => clearTimeout(t); } }, [screen]);

  return (
    <div className="min-h-screen bg-warm-cream flex flex-col items-center px-5 py-8" data-testid="public-checkin">
      <img src="/logo.png" alt="Rosemoor" className="w-16 h-16 mb-3" />
      <h1 className="font-zilla text-3xl font-semibold text-center">Welcome to Rosemoor</h1>
      {ctx?.active && ctx.event_title && <p className="text-dark-olive font-semibold text-[16px] mt-1" data-testid="station-event">{ctx.event_title}</p>}
      <p className="text-ink/60 text-[15px] mb-7">Check in below</p>

      <div className="w-full max-w-sm">
        {ctx && !ctx.active ? (
          <div className="text-center py-12" data-testid="checkin-closed">
            <div className="w-16 h-16 rounded-full bg-gold/25 text-ink flex items-center justify-center mx-auto mb-4 text-3xl">⏳</div>
            <h2 className="font-zilla text-2xl font-semibold mb-2">Check-in isn't open yet</h2>
            <p className="text-ink/65 text-[16px]">Please ask a volunteer at the welcome table to start check-in, then try again.</p>
          </div>
        ) : screen === "done" ? (
          <Success name={name} already={already} onDone={reset} />
        ) : screen === "household" ? (
          <div>
            <div className="text-center mb-2"><h2 className="font-zilla text-2xl font-semibold">You're checked in{name ? `, ${name}` : ""}!</h2></div>
            <HouseholdCheckinPrompt memberId={household.memberId} household={household.list} onDone={() => setScreen("done")} />
          </div>
        ) : screen === "roles" && roles ? (
          <RolePicker data={roles} onDone={(d) => { setRoles(null); done(d); }} onCancel={reset} />
        ) : screen === "choose" ? (
          <div className="space-y-3">
            <button data-testid="choose-member" onClick={() => setScreen("member")} className={`${bigBtn} bg-ink text-cream`}>I'm a Member</button>
            <button data-testid="choose-returning" onClick={() => setScreen("returning")} className={`${bigBtn} bg-cream border border-ink/25 text-ink`}>I'm a Returning Visitor</button>
            <button data-testid="choose-new" onClick={() => setScreen("new")} className={`${bigBtn} bg-cream border border-ink/25 text-ink`}>I'm a New Visitor</button>
            <button data-testid="choose-guest" onClick={() => setScreen("guest")} className={`${bigBtn} bg-cream border border-ink/25 text-ink`}>I'm a Guest of a Business</button>
          </div>
        ) : (
          <div>
            <button data-testid="checkin-back" onClick={reset} className="text-dark-olive font-semibold text-[15px] mb-4">← Back</button>
            {screen === "member" && <MemberForm onSuccess={done} />}
            {screen === "returning" && <ReturningForm onSuccess={done} onFallback={(f) => { setFallback(f); setScreen("new"); }} />}
            {screen === "new" && <NewVisitorForm initial={fallback} onSuccess={done} />}
            {screen === "guest" && <GuestForm onSuccess={done} />}
          </div>
        )}
      </div>
    </div>
  );
}
